/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import {
	createContext,
	type Dispatch,
	type SetStateAction,
	useContext,
	useMemo,
	useState,
} from 'react';

import type {
	ActivationCapability,
	Edition,
	PriceSegmentation,
	ProductId,
} from '../../../services/modal-data/types';
import type { ModalState, ScreenState } from '../common/types';

/**
 * Context for sharing state and configuration across modal screens.
 * Contains only state/configuration fields.
 */
export interface ModalContextValue {
	cloudId: string;
	currentScreen: ScreenState;
	isNormalizeSeatsEnabled: boolean;
	locale: string;
	modalState: ModalState;
	orgId?: string;
	redirectUrl: string;
	setCurrentScreen: Dispatch<SetStateAction<ScreenState>>;
	setIsNormalizeSeatsEnabled: Dispatch<SetStateAction<boolean>>;
	setModalState: Dispatch<SetStateAction<ModalState>>;
	setRedirectUrl: Dispatch<SetStateAction<string>>;
	sourceComponent: string;
	sourceContext: string;
}

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

export const ModalProvider = ModalContext.Provider;

export const useModalContext = (): ModalContextValue => {
	const ctx = useContext(ModalContext);
	if (!ctx) {
		throw new Error('useModalContext must be used within ModalProvider');
	}
	return ctx;
};

type ModalPresets = {
	initialIsNormalizeSeatsEnabled?: boolean;
	initialModalState?: Partial<ModalState>;
	initialRedirectUrl?: string;
	initialScreen?: ScreenState;
};

/**
 * Hook to create and manage modal state for the context provider.
 * Initializes all state values and returns a memoized context value.
 */
export const useModalState = (
	cloudId: string,
	sourceComponent: string,
	sourceContext: string,
	locale: string,
	orgId?: string,
	presets?: ModalPresets,
): ModalContextValue => {
	const [currentScreen, setCurrentScreen] = useState<ScreenState>(
		presets?.initialScreen ?? 'loading',
	);
	const [redirectUrl, setRedirectUrl] = useState<string>(
		presets?.initialRedirectUrl ?? 'https://home.atlassian.com/',
	);
	const [modalState, setModalState] = useState<ModalState>(
		() =>
			({
				edition: 'premium' as Edition,
				hasPaymentDetails: true,
				userProducts: [] as ProductId[],
				priceSegmentation: 'increase' as PriceSegmentation,
				priceValue: undefined as number | undefined,
				activationCapability: 'can-activate' as ActivationCapability,
				...presets?.initialModalState,
			}) as ModalState,
	);
	const [isNormalizeSeatsEnabled, setIsNormalizeSeatsEnabled] = useState<boolean>(
		presets?.initialIsNormalizeSeatsEnabled ?? true,
	);

	const contextValue = useMemo(
		() => ({
			cloudId,
			locale,
			sourceComponent,
			sourceContext,
			currentScreen,
			setCurrentScreen,
			modalState,
			setModalState,
			orgId,
			redirectUrl,
			setRedirectUrl,
			isNormalizeSeatsEnabled,
			setIsNormalizeSeatsEnabled,
		}),
		[
			cloudId,
			locale,
			sourceComponent,
			sourceContext,
			currentScreen,
			modalState,
			orgId,
			redirectUrl,
			isNormalizeSeatsEnabled,
		],
	);

	return contextValue;
};
